import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { getCached, setCached, invalidateCache } from '@/hooks/useCachedFetch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BrandContext = createContext(null);

export const PLAN_LABELS = {
  cove:    'Cove',
  harbor:  'Harbor',
  voyage:  'Voyage',
  admiral: 'Admiral',
};

export const PLAN_COLORS = {
  cove:    'text-gray-400',
  harbor:  'text-blue-400',
  voyage:  'text-purple-400',
  admiral: 'text-amber-400',
};

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const [activeBrand, setActiveBrandState] = useState(null);
  const [brands, setBrands]               = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [showNewBrandModal, setShowNewBrandModal] = useState(false);

  const fetchBrands = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoadingBrands(false); return; }

    // Tenta cache primeiro
    const cached = getCached('brands:list');
    if (cached) {
      setBrands(cached);
      const savedId = localStorage.getItem('activeBrandId');
      setActiveBrandState(cached.find(b => b.id === savedId) || cached[0] || null);
      setLoadingBrands(false);
      return;
    }

    try {
      const res  = await axios.get(`${API}/brands`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data || [];
      setCached('brands:list', list);
      setBrands(list);

      const savedId = localStorage.getItem('activeBrandId');
      const found   = list.find(b => b.id === savedId);
      setActiveBrandState(found || list[0] || null);
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
      // O 401 NAO E TRATADO AQUI desde 10/09/2026: o interceptor do axios
      // (App.js) ja o recebeu e encerrou a sessao por `sessaoVencida`. Este era o
      // unico caminho de 401 para o login no frontend inteiro, e so rodava na
      // montagem — uma aba aberta nunca chegava nele. Mante-lo seria um segundo
      // lugar decidindo o fim da sessao.
      if (error?.response?.status !== 401) {
        toast.error('Erro ao carregar marcas. Tente novamente.');
      }
    } finally {
      setLoadingBrands(false);
    }
  }, []);

  // Re-carregar quando o usuário faz login ou logout
  useEffect(() => {
    // O usuário da revenda (PROMPT 215) só tem o portal: a API de marcas recusa, e pedir mostraria erro à toa.
    if (user?.role === 'revenda') {
      setLoadingBrands(false);
    } else if (user) {
      fetchBrands();
    } else {
      invalidateCache(); // limpa todo o cache ao fazer logout
      setBrands([]);
      setActiveBrandState(null);
      setLoadingBrands(false);
    }
  }, [user, fetchBrands]);

  const setActiveBrand = useCallback((brand) => {
    setActiveBrandState(brand);
    if (brand?.id) {
      localStorage.setItem('activeBrandId', brand.id);
      invalidateCache(`brand:${brand.id}`); // invalida cache específico da brand anterior
    }
  }, []);

  // O `activeBrandId` DO STORAGE SEGUE A MARCA ATIVA, SEMPRE.
  //
  // Ate 09/09/2026 so o `setActiveBrand` acima gravava — ou seja, so o CLIQUE
  // no seletor. A selecao automatica das linhas 40 e 55 usa o setter cru e nao
  // gravava nada. Em conta de UMA marca so, o gestor nunca tem motivo para
  // clicar, entao o storage ficava com o valor de outra conta, de uma sessao
  // anterior, ou vazio.
  //
  // O interceptor do axios (App.js:87) le o STORAGE, nao o estado do React. Com
  // os dois divergindo, a tela mostrava "Arkipel • MKT" e o header mandava a
  // marca da Cizor: `marca_da_requisicao` recusava com "[BRAND] header nao
  // pertence a conta", e `/meta/status` respondia `connected: false` com a Meta
  // conectada no banco. A aba Integracoes nao sofria porque recebe a marca por
  // query param, do seletor do proprio dialogo.
  //
  // SO GRAVA, NUNCA APAGA, e isso e decisao e nao esquecimento. `activeBrand`
  // nasce `null`, e ha caminhos em que `loadingBrands` ja e `false` com ele
  // ainda nulo — lista vazia, cache vazio, o `catch` do 401. Um ramo `else` com
  // `removeItem` apagaria a chave nesses instantes e o header voltaria a nao
  // viajar: a mesma armadilha de ordem de efeito que custou o historico do
  // Estrategista hoje de manha. Sem o `else`, o pior caso e a chave ficar com o
  // valor velho — que e exatamente o estado de antes desta mudanca.
  //
  // A limpeza do id morto fica como divida, com o teste que prova quando
  // `loadingBrands` vira `false`.
  useEffect(() => {
    if (activeBrand?.id) {
      localStorage.setItem('activeBrandId', activeBrand.id);
    }
  }, [activeBrand?.id]);

  const refreshBrands = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res  = await axios.get(`${API}/brands`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data || [];
      setCached('brands:list', list); // atualiza cache
      setBrands(list);
      setActiveBrandState(prev =>
        (prev ? list.find(b => b.id === prev.id) : null) || list[0] || null
      );
    } catch {}
  }, []);

  return (
    <BrandContext.Provider value={{
      activeBrand,
      setActiveBrand,
      brands,
      setBrands,
      loadingBrands,
      refreshBrands,
      showNewBrandModal,
      setShowNewBrandModal,
    }}>
      {children}
    </BrandContext.Provider>
  );
}

export const useBrand = () => {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used inside BrandProvider');
  return ctx;
};
